import { useTranslation } from "react-i18next";
import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { openCookiePreferences } from "@/components/CookieConsent";

export default function PrivacyPolicy() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const c = COPY[lang];

  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        titleA={c.titleA}
        titleB={c.titleB}
        description={c.description}
        imageUrl="/eu-heritage-hero.png"
      />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-10 text-[15px] text-[#1e293b] leading-[1.85]">
          {c.sections.map((s, i) => (
            <div key={i} className="space-y-3">
              <h2 className="font-display text-[26px] md:text-[30px] text-[#0f2340] leading-tight">
                {s.h}
              </h2>
              {s.p.map((para, j) => (
                <p key={j}>{para}</p>
              ))}
              {s.list && (
                <ul className="list-disc pl-6 space-y-1.5 text-[#334155]">
                  {s.list.map((li, k) => (
                    <li key={k}>{li}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="border-t border-[#e2e8f0] pt-8 mt-8">
            <button
              data-testid="reopen-cookie-preferences-btn"
              onClick={openCookiePreferences}
              className="text-[11px] tracking-[0.28em] uppercase text-[#1a365d] hover:text-[#d4af37] transition-colors font-semibold underline decoration-[#d4af37] decoration-2 underline-offset-[6px]"
            >
              {c.managePreferences}
            </button>
          </div>

          <p className="text-[12.5px] text-[#64748b] pt-6">{c.lastUpdated}</p>
        </div>
      </section>
    </PageShell>
  );
}

const COPY = {
  it: {
    eyebrow: "— Legal / Privacy Policy",
    titleA: "Informativa",
    titleB: "sulla Privacy.",
    description:
      "Tesori d'Europa protegge la tua privacy e tratta i tuoi dati personali con la massima cura, in conformità al Regolamento (UE) 2016/679 (GDPR) e alle normative statali statunitensi applicabili.",
    managePreferences: "Gestisci le mie preferenze sui cookie",
    lastUpdated: "Ultimo aggiornamento: Febbraio 2026.",
    sections: [
      {
        h: "1. Titolare del trattamento",
        p: [
          "Il titolare del trattamento dei dati personali è Tesori d'Europa, con sede in Roma, contattabile all'indirizzo info@tesorideuropa.com.",
        ],
      },
      {
        h: "2. Dati raccolti",
        p: ["Raccogliamo esclusivamente i dati necessari per le seguenti finalità:"],
        list: [
          "Dati di contatto forniti tramite form (nome, email, telefono, paese) per rispondere alle richieste degli utenti.",
          "Dati forniti in occasione della partecipazione al photo contest \"In Between\" (dati anagrafici, immagini, descrizioni del progetto).",
          "Dati di navigazione tecnica raccolti tramite cookie strettamente necessari (log server, indirizzo IP anonimizzato).",
          "Dati statistici aggregati raccolti tramite cookie di misurazione, solo previo tuo consenso.",
        ],
      },
      {
        h: "3. Base giuridica",
        p: [
          "Il trattamento avviene sulla base del tuo consenso (art. 6.1.a GDPR), per l'esecuzione di misure precontrattuali su tua richiesta (art. 6.1.b) e per il legittimo interesse del Titolare a proteggere e migliorare il servizio (art. 6.1.f).",
        ],
      },
      {
        h: "4. Modalità di trattamento",
        p: [
          "I dati sono trattati con strumenti elettronici, con misure di sicurezza tecniche e organizzative adeguate a prevenire la perdita, l'accesso non autorizzato o l'uso illecito.",
          "I dati sono conservati per il tempo strettamente necessario alle finalità dichiarate e, comunque, non oltre 24 mesi dalla loro raccolta salvo obblighi di legge.",
        ],
      },
      {
        h: "5. Diritti dell'utente (GDPR)",
        p: ["In qualsiasi momento hai diritto a:"],
        list: [
          "Accedere ai tuoi dati personali e chiederne una copia.",
          "Rettificarli, aggiornarli o cancellarli (\"diritto all'oblio\").",
          "Limitare o opporti al loro trattamento.",
          "Ricevere i tuoi dati in formato strutturato e portabile.",
          "Revocare il consenso in qualsiasi momento, senza pregiudicare la liceità del trattamento precedente.",
          "Proporre reclamo al Garante per la Protezione dei Dati Personali.",
        ],
      },
      {
        h: "6. Diritti dei residenti negli Stati Uniti",
        p: [
          "Se risiedi in uno stato degli Stati Uniti che riconosce diritti specifici sulla privacy (California, Virginia, Colorado, Connecticut, Utah e altri), puoi esercitare tali diritti — inclusi il diritto di rinunciare alla vendita e alla condivisione dei tuoi dati e alla pubblicità mirata — tramite il pannello \"Le tue scelte sulla privacy\" accessibile dal footer.",
        ],
      },
      {
        h: "7. Comunicazione a terzi",
        p: [
          "Non vendiamo i tuoi dati personali. Possiamo condividerli con fornitori tecnici (hosting, email transazionali, analytics) esclusivamente per finalità operative e nel rispetto della normativa vigente.",
        ],
      },
      {
        h: "8. Trasferimenti extra-UE",
        p: [
          "Eventuali trasferimenti di dati al di fuori dello Spazio Economico Europeo avvengono nel rispetto delle Clausole Contrattuali Standard approvate dalla Commissione Europea.",
        ],
      },
      {
        h: "9. Modifiche",
        p: [
          "La presente informativa può essere aggiornata in qualsiasi momento. Le eventuali modifiche saranno pubblicate su questa pagina con la data di ultimo aggiornamento.",
        ],
      },
      {
        h: "10. Contatti",
        p: [
          "Per qualsiasi domanda relativa al trattamento dei tuoi dati o per esercitare i tuoi diritti, scrivi a: info@tesorideuropa.com.",
        ],
      },
    ],
  },
  en: {
    eyebrow: "— Legal / Privacy Policy",
    titleA: "Privacy",
    titleB: "Policy.",
    description:
      "Tesori d'Europa protects your privacy and processes your personal data with the utmost care, in compliance with Regulation (EU) 2016/679 (GDPR) and applicable US state privacy laws.",
    managePreferences: "Manage my cookie preferences",
    lastUpdated: "Last updated: February 2026.",
    sections: [
      {
        h: "1. Data Controller",
        p: [
          "The data controller is Tesori d'Europa, based in Rome, reachable at info@tesorideuropa.com.",
        ],
      },
      {
        h: "2. Data we collect",
        p: ["We only collect the data necessary for the following purposes:"],
        list: [
          "Contact data provided via forms (name, email, phone, country) to respond to user requests.",
          "Data provided when participating in the \"In Between\" photo contest (personal details, images, project description).",
          "Technical navigation data collected via strictly necessary cookies (server logs, anonymized IP).",
          "Aggregated statistical data collected via measurement cookies, only with your consent.",
        ],
      },
      {
        h: "3. Legal basis",
        p: [
          "Processing takes place on the basis of your consent (Art. 6.1.a GDPR), for pre-contractual measures at your request (Art. 6.1.b), and for the Controller's legitimate interest in protecting and improving the service (Art. 6.1.f).",
        ],
      },
      {
        h: "4. Processing methods",
        p: [
          "Data are processed with electronic tools, with technical and organizational security measures adequate to prevent loss, unauthorized access or unlawful use.",
          "Data are stored for the time strictly necessary for the stated purposes and, in any case, no longer than 24 months from collection unless required by law.",
        ],
      },
      {
        h: "5. Your rights (GDPR)",
        p: ["At any time you have the right to:"],
        list: [
          "Access your personal data and request a copy.",
          "Rectify, update or erase them (\"right to be forgotten\").",
          "Restrict or object to their processing.",
          "Receive your data in a structured, portable format.",
          "Withdraw consent at any time, without affecting the lawfulness of previous processing.",
          "Lodge a complaint with the competent Data Protection Authority.",
        ],
      },
      {
        h: "6. Rights of US residents",
        p: [
          "If you reside in a US state that recognizes specific privacy rights (California, Virginia, Colorado, Connecticut, Utah and others), you can exercise those rights — including the right to opt out of the sale and sharing of your data and targeted advertising — through the \"Your Privacy Choices\" panel accessible from the footer.",
        ],
      },
      {
        h: "7. Third-party sharing",
        p: [
          "We do not sell your personal data. We may share them with technical providers (hosting, transactional email, analytics) exclusively for operational purposes and in compliance with applicable regulations.",
        ],
      },
      {
        h: "8. Extra-EU transfers",
        p: [
          "Any transfer of data outside the European Economic Area takes place under the Standard Contractual Clauses approved by the European Commission.",
        ],
      },
      {
        h: "9. Changes",
        p: [
          "This policy may be updated at any time. Any changes will be published on this page with the last-updated date.",
        ],
      },
      {
        h: "10. Contact",
        p: [
          "For any question about the processing of your data or to exercise your rights, write to: info@tesorideuropa.com.",
        ],
      },
    ],
  },
};
